
import { Component, Watch, Mixins } from "vue-property-decorator";
import { quillEditor } from "vue-quill-editor";
// require styles 富文本编辑器对应的样式
import "quill/dist/quill.core.css";
import "./quill.snow.css";
import { IArticle } from "@/common/entity/Article";
import {
  ActionType,
  CountResultTip,
  CalculateStatus,
  CheckPageParams,
  correctionMarkId,
  ICorrectInfo,
  Contents,
} from "./CheckPage";
import { CorrectStatus } from "@/common/entity/Correction";
import {
  ChangeSource,
  EventName,
  QuillEditor,
} from "@/common/entity/QuillEditor";
import {
  appColors,
  qEditorDefaultOptions,
  SessionStorageKey,
} from "@/common/AppConst";
import {
  CorrectInfos,
  CorrectInfosOptions,
  RootGetterNames,
  RootMutationNames,
  RootState,
  RootStateNames,
} from "@/vuex-store";
import { COUNT_NO_LIMIT, User } from "@/common/entity/User";
import { Getter, Mutation, State } from "vuex-class";
import { logToConsole, logToServer } from "@/common/LogHelper";
import CountTextLenMixin from "./CountTextLenMixin";
import CorrectMarkMixin from "./CorrectMarkMixin";
import {
  htmlStartWithWrap,
  htmlTextSplitLines,
  isEmpty,
} from "@/common/StringHelper";
import { Message } from "element-ui";
import { CorrectOpType } from "@/common/entity/CorrectResult";
import { doCorrection } from "@/common/CorrectHelper";
import {
  ArticleApis,
  CorrectionRespData,
  CorrectParams,
  CorrectRespData,
  LongCorrectRespData,
  normalizeCorrections,
  resolveCorrectRespCorrections,
  resolveCorrectRespUser,
  MSG_CORRECT_RESP_FORMAT_ERR,
  validateCorrectRespData,
  validateLongCorrectRespData,
  validateCorrectionQueryRespData,
} from "@/request/article";
import { RouterTools } from "@/common/RouterTools";
import { Response } from "@/common/entity/Response";
import { debounce } from "vue-debounce";

/** 短文本模式单次纠错最大字数 */
const SHORT_TEXT_MAX_LEN = 3000;
/** 文章标题最大字数 */
const ARTICLE_TITLE_MAX_LEN = 50;

@Component({
  components: {
    quillEditor,
  },
})
export default class Check extends Mixins(CountTextLenMixin, CorrectMarkMixin) {
  /**
   * 编辑器配置
   */
  readonly qEditorOptions = {
    ...qEditorDefaultOptions,
    placeholder: "请输入文章内容...",
  };
  readonly CorrectOpType = CorrectOpType;
  readonly articleTitleMaxLen = ARTICLE_TITLE_MAX_LEN;

  /**
   * 显示多篇文章模式下的文章map，key为文章id，
   * value为文章实例本身
   */
  articleMap: { [key: number]: IArticle } = {};
  /**
   * 保存成功后的文章
   */
  savedArticle: IArticle | null = null;
  /**
   * 标题输入框内容
   */
  currTitle: string = "";
  /**
   * 当前操作类型，创建文章、查看范文或编辑文章
   */
  actionType: ActionType = ActionType.create;
  /**
   * 当前是否是长文本模式
   */
  isLongTextModel: boolean = false;
  /**
   * 计算纠错次数后，内容是否已经更改
   */
  isUpdateSinceCalculate: boolean = false;
  /**
   * 导致计算纠错次数失败的文本，一般是"单句"文本过长时的"单句“文本
   */
  failText: string | null = null;
  /**
   * 是否正在纠错中
   */
  correcting: boolean = false;

  /**
   * 当前是否已经是登录状态
   */
  @Getter(RootGetterNames.isLogined)
  isLogined!: boolean;

  /** 内容状态值 */
  @State(RootStateNames.correctInfos)
  readonly storeInfos!: CorrectInfos;

  /** 更新内容状态值 */
  @Mutation(RootMutationNames.updateCorrectInfos)
  readonly updateStoreInfos!: (infos: CorrectInfosOptions | null) => void;

  /**
   * 短文本模式单次纠错最大字数（覆盖 CountTextLenMixin 中的服务端配置）
   */
  get maxLenOnceCorrect(): number {
    return SHORT_TEXT_MAX_LEN;
  }

  /**
   * 是否显示多文章模式
   */
  get showArticleSelector(): boolean {
    return Object.keys(this.articleMap).length > 1;
  }

  /**
   * 当前是否为检测范例模式
   */
  get isExampleMode(): boolean {
    return this.actionType === ActionType.example;
  }

  /**
   * 当前登录用户
   */
  get currUser(): User | null {
    return (this.$store.state as RootState).currUser;
  }

  /**
   * 是否显示纠错按钮
   */
  get showCorrectBtn(): boolean {
    return !this.savedArticle || !this.savedArticle.correct_text;
  }

  /**
   * 检测出结果后，标题右侧显示新建纠错按钮（范文模式已有「我要纠错」，不重复显示）
   */
  get showNewCorrectBtn(): boolean {
    return !this.showCorrectBtn && !this.isExampleMode;
  }

  /**
   * 是否把纠错按钮设置为不可点击状态
   */
  get disableCorrectBtn(): boolean {
    return (
      this.isLongTextModel &&
      this.calculateCountStatus !== CalculateStatus.success
    );
  }

  /**
   * 编辑器下方文本长度、计算消耗的纠错次数和计算失败时的提示信息
   */
  get textLenTips(): CountResultTip {
    if (!this.showCorrectBtn) return this.onlyTotalLenTipObj;
    if (this.isLongTextModel) {
      //计算纠错次数后，文本发生更改时
      if (this.isUpdateSinceCalculate) return this.calculateIngTipObj;
      //根据计算状态，显示对应的提示内容
      switch (this.calculateCountStatus) {
        case CalculateStatus.doing:
          return this.calculateIngTipObj;
        case CalculateStatus.fail:
          return this.calculateFailTipObj || this.defaultCalculateFailTipObj;
        case CalculateStatus.success:
          if (this.correctCountToPay > this.maxPayCountOnce)
            return this.overPayCountTipObj;
          else return this.normalPayCountTipObj;
      }
    } else {
      if (this.textLength > this.maxLenOnceCorrect)
        return this.overTextLenTipObj;
      else return this.normalTextLenTipObj;
    }
  }

  /**
   * 显示文本长度结果的提示style
   */
  get textLenTipsStyle(): object {
    return {
      display: "flex",
      "justify-content": "flex-end",
      margin: "10px 0px",
      cursor: this.isLongTextModel ? "pointer" : "auto",
      color: this.textLenTips.color,
    };
  }

  /**
   * 编辑器下方显示{@link failText}异常文本面板的标题
   */
  get failTextTitle(): string {
    const textLen = isEmpty(this.failText || "") ? 0 : this.failText?.length;
    return `错误位置(点击可在编辑器中选中对应文本(字数：${textLen}))`;
  }

  /**
   * 显示在右边的纠错信息
   */
  get correctInfos(): ICorrectInfo[] {
    if (!this.correctResults) return [];
    const infos: ICorrectInfo[] = [];
    this.correctResults.forEach((correctionsInLine, firstIndex) => {
      correctionsInLine.forEach((correction, secondIndex) => {
        if (!correction.show) return;
        const correctInfo: ICorrectInfo = {
          ...correction,
          firstIndex,
          secondIndex,
        };
        infos.push(correctInfo);
      });
    });
    return infos;
  }

  @Watch("actionType")
  onActionTypeChange(type: ActionType) {
    logToConsole("actionType更新：", type);
    sessionStorage.setItem(SessionStorageKey.checkActionType, type + "");
  }

  @Watch("savedArticle", { deep: true })
  onSavedArticleChange(article: IArticle | null, oldArticle: IArticle | null) {
    if (article === oldArticle) return;
    if (article?.id === oldArticle?.id) return;
    if (article)
      sessionStorage.setItem(SessionStorageKey.checkArticleId, article.id + "");
    else sessionStorage.removeItem(SessionStorageKey.checkArticleId);
  }

  @Watch("currTitle")
  onCurrTitleChange(title: string) {
    if (this.actionType === ActionType.create) this.updateStoreInfos({ title });
  }

  mounted() {
    // 获取本地存储中保存的检测界面操作类型
    const actionTypeStr = sessionStorage.getItem(
      SessionStorageKey.checkActionType
    );
    const actionType: number | null = actionTypeStr
      ? parseInt(actionTypeStr)
      : null;

    // 获取本地存储中保持的检测界面的文章id
    const articleIdStr = sessionStorage.getItem(
      SessionStorageKey.checkArticleId
    );
    let articleId: number | null = articleIdStr ? parseInt(articleIdStr) : null;

    // 获取路由参数中的操作类型和文章id
    const params: CheckPageParams = this.$route.params as CheckPageParams;
    logToConsole("检测界面 mounted params=", params);
    logToConsole("检测界面 mounted storage params=", {
      actionType,
      articleId,
    });

    //确定操作类型和文章id
    this.actionType =
      (typeof params.type === "undefined"
        ? actionType
        : parseInt(params.type)) || ActionType.create;
    articleId =
      typeof params.articleId === "undefined"
        ? articleId
        : parseInt(params.articleId);

    //重新保存操作类型和文章id到sessionStorage
    if (this.actionType)
      sessionStorage.setItem(
        SessionStorageKey.checkActionType,
        this.actionType + ""
      );
    else sessionStorage.removeItem(SessionStorageKey.checkActionType);
    if (articleId)
      sessionStorage.setItem(SessionStorageKey.checkArticleId, articleId + "");
    else sessionStorage.removeItem(SessionStorageKey.checkArticleId);

    //根据操作类型进行对应操作
    switch (this.actionType) {
      case ActionType.example: //范文
        this.getDefaultArticle();
        break;
      case ActionType.edit: //编辑文章
        if (this.isLogined && articleId) this.article(articleId);
        break;
      case ActionType.create: // 创建文章（或返回正在创建的文章）
        if (!this.storeInfos) break;
        this.loadStoreInfos();
        break;
    }
  }

  /**
   * 检测范例模式下，进入纠错页面
   */
  goToCorrectPage() {
    this.resetPage(ActionType.create);
    sessionStorage.removeItem(SessionStorageKey.checkArticleId);
    if (this.storeInfos) this.loadStoreInfos();
    RouterTools.pushTo(RouterTools.pageNames.check, {
      type: ActionType.create + "",
    });
  }

  /**
   * 检测出结果后，新建一篇纠错
   */
  goToNewCorrectPage() {
    this.resetPage(ActionType.create);
    sessionStorage.removeItem(SessionStorageKey.checkArticleId);
    this.updateStoreInfos(null);
  }

  /**
   * 点击{@link failText}时调用
   */
  clickFailText() {
    if (!this.failText) return;
    const text = this.getText();
    logToConsole("clickFailText editor text=", text);
    if (isEmpty(text)) return;
    const failIndex = text.indexOf(this.failText);
    this.qEditor?.setSelection(failIndex, this.failText.length);
  }
  /**
   * 编辑器ready
   */
  onQEditorReady(editor: QuillEditor) {
    this.qEditor = editor;
    this.qEditor.clipboard.addMatcher(
      Node.ELEMENT_NODE,
      (node: any, delta: any) => {
        if (!this.matcherIsRun) return delta;
        logToConsole("delta", delta);
        let ops: any = [];
        delta.ops.forEach((op: any) => {
          if (op.insert && typeof op.insert === "string")
            ops.push({ insert: op.insert });
        });
        delta.ops = ops;
        return delta;
      }
    );
    this.qEditor.on(EventName.selectionChange, (range, oldRange, source) => {
      logToConsole("range", range);
      logToConsole("oldRange", oldRange);
      logToConsole("source", source);
    });
    this.qEditor.on(EventName.textChange, (delta, oldDelta, source) => {
      if (this.actionType === ActionType.create) {
        let contentHtml = this.getQEditorCurrHtml();
        if (contentHtml == "<p><br></p>") contentHtml = "";
        this.updateStoreInfos({ contentHtml });
      }
      if (source == ChangeSource.user && this.hasCorrections()) {
        this.userDeltas.unshift(delta);
        logToConsole("userDeltas update newDelta = ", delta);
      }
      if (source == ChangeSource.user) this.failText = null;
      if (!this.showCorrectBtn) return;
      this.debounceTextLength();
      if (this.isLongTextModel) this.debouncePayCount();
    });
  }

  /**
   * 点击纠错信息列表项时调用
   * @param firstIndex 点击的纠错信息对应在{@link correctResults}数组中的第一层的下标
   * @param secondIndex 点击的纠错信息对应在{@link correctResults}数组中的第而层的下标
   */
  clickCorrectItem(firstIndex: number, secondIndex: number) {
    logToConsole("clickCorrectItem firstIndex=", firstIndex);
    logToConsole("clickCorrectItem secondIndex=", secondIndex);
    const corrections = this.correctResults;
    if (!corrections[firstIndex] || !corrections[firstIndex][secondIndex]) {
      this.showNotFound(firstIndex);
      return;
    }
    //找出对应纠错信息
    const clickCorrection = corrections[firstIndex][secondIndex];
    this.qEditor?.setSelection(clickCorrection.indexTotal);
    logToConsole("clickCorrectItem correction=", clickCorrection);
    this.highLightCorrection(clickCorrection, firstIndex, secondIndex);
  }

  /**
   * 点击纠错信息列表项的替换词时调用
   * @param firstIndex 点击的纠错信息对应在{@link correctResults}数组中的第一层的下标
   * @param secondIndex 点击的纠错信息对应在{@link correctResults}数组中的第而层的下标
   */
  clickCorrectText(firstIndex: number, secondIndex: number) {
    const corrections = this.correctResults;
    if (!corrections[firstIndex][secondIndex]) return;
    //找出对应纠错信息
    const clickCorrection = corrections[firstIndex][secondIndex];
    this.qEditor?.setSelection(clickCorrection.indexTotal);
    //切割html段落
    this.updateAndRemarkHtml();
    const htmlLines = htmlTextSplitLines(this.correctHtml, true);
    const indexOffset = htmlStartWithWrap(this.correctHtml) ? 1 : 0;
    const lineIndex = (firstIndex + indexOffset) * 2;
    const htmlLine = htmlLines[lineIndex];
    //替换内容
    logToConsole("clickCorrectText before correction htmlLine=", htmlLine);
    const newHtmlLine = doCorrection(
      htmlLine,
      clickCorrection,
      correctionMarkId(firstIndex, secondIndex)
    );
    logToConsole("clickReplaceBtn after correction newHtmlLine=", newHtmlLine);
    if (newHtmlLine) htmlLines[lineIndex] = newHtmlLine;
    this.correctHtml = htmlLines.join("");
    //更新纠错信息，隐藏纠错item
    clickCorrection.show = false;
    this.correctResults = corrections;
    this.setQeditorHtml(this.correctHtml);
  }

  /**
   * 多篇文章的情况下，切换文章时调用
   */
  onArticleSelected(articleId: number) {
    logToConsole("检测界面 onArticleSelected articleId=", articleId);
    this.initArticleContent(this.articleMap[articleId]);
  }

  /**
   * 显示多篇文章模式下，设置文章列表
   */
  setArticleList(articles: IArticle[]) {
    logToConsole("检测界面 setArticleList articles=", articles);
    this.articleMap = {};
    let firstId: number | null = null;
    articles.forEach((article) => {
      if (!article.id) return;
      if (firstId === null) firstId = article.id;
      this.articleMap[article.id] = article;
    });
    if (firstId) this.onArticleSelected(firstId);
  }

  /**
   * 根据文章初始化当前界面
   */
  initArticleContent(savedArticle: IArticle) {
    this.currHighLight = null;
    this.savedArticle = savedArticle;
    this.currTitle = savedArticle.title || "";
    this.markCorrections(
      normalizeCorrections(savedArticle.correction),
      savedArticle.content || ""
    );
  }

  /**
   * 获取并显示默认文章
   */
  async getDefaultArticle() {
    this.setArticleList(await ArticleApis.defaults());
  }

  /**
   * 获取并显示id为{@link articleId}的文章
   */
  async article(articleId: number) {
    this.setArticleList([await ArticleApis.query(articleId)]);
  }

  /**
   * 保存文章
   */
  async save() {
    if (this.showNotLogin()) return;
    const currContent = this.parseCurrContent();
    if (this.showTitleContentEmpty(this.currTitle, currContent)) return;
    const articleId = this.savedArticle ? this.savedArticle.id : undefined;
    logToConsole("savedArticle", this.savedArticle);
    try {
      this.savedArticle = await ArticleApis.save(
        {
          id: articleId,
          title: this.currTitle,
          content: currContent.html,
        },
        false
      );
      if (this.actionType === ActionType.create) this.updateStoreInfos(null);
      this.actionType = ActionType.edit;
      Message.success({
        message: "文章保存成功!",
        center: true,
        showClose: true,
      });
    } catch (error) {
      this.showUserApiErr(error, "保存失败");
    }
  }

  /** 重置当前页面 */
  resetPage(actionType: ActionType) {
    this.articleMap = {};
    this.savedArticle = null;
    this.currTitle = "";
    this.actionType = actionType;
    this.correctResults = [];
    this.currHighLight = null;
    this.correcting = false;
    this.failText = null;
    this.setQeditorToEmpty();
  }

  /** 从{@link storeInfos}中加载内容 */
  loadStoreInfos() {
    this.currTitle = this.storeInfos.title;
    this.isLongTextModel = false;
    this.setQeditorHtml(this.storeInfos.contentHtml);
  }

  /**
   * 纠错
   */
  async check() {
    // 检查登录状态
    if (this.showNotLogin()) return;

    // 获取文章内容
    const currContent: Contents = this.parseCurrContent();
    logToConsole("check currContent=", currContent);

    // 检查文章内容是否合规
    if (this.showTitleContentEmpty(this.currTitle, currContent)) return;
    if (this.showTextLenLimit()) return;

    // 提示消耗和剩余的纠错次数
    if (!(await this.showCountConfirm())) {
      RouterTools.pushTo("pay");
      return;
    }

    this.correcting = true;

    // 纠错接口参数
    const params: CorrectParams = {
      articleId: this.savedArticle?.id,
      content: currContent.html,
      text: currContent.text,
      title: this.currTitle,
    };

    // 纠错请求
    try {
      const payCount = this.isLongTextModel ? this.correctCountToPay : 1;
      if (this.isLongTextModel) await this.longTextCorrect(params, payCount);
      else await this.shortTextCorrect(params, payCount);
    } catch (error: Response | any) {
      // 纠错失败
      this.showCorrectErr(error);
    }
  }

  /**
   * 发送非长文本纠错请求
   * @param params 请求参数
   * @param payCount 本次纠错消耗的纠错次数
   */
  async shortTextCorrect(params: CorrectParams, payCount: number) {
    const respData = validateCorrectRespData(
      await ArticleApis.correct(params, false)
    );
    this.onCorrectSuccess(payCount, respData);
  }

  /**
   * 发送长文本纠错请求并轮循查询结果
   * @param params 请求参数
   * @param payCount 本次纠错消耗的纠错次数
   */
  async longTextCorrect(params: CorrectParams, payCount: number) {
    const respData = validateLongCorrectRespData(
      await ArticleApis.longCorrect(params, false)
    );
    this.actionType = ActionType.edit;
    const correctionId = respData.correction.id!;
    setTimeout(() => {
      this.queryCorrection(correctionId, payCount).catch((error) => {
        this.showCorrectErr(error);
      });
    }, respData.minFinishTimeMS || 1000);
  }

  /**
   * 查询纠错记录
   * @param correctionId 纠错记录id
   * @param payCount 要查询的纠错记录id消耗了的纠错次数
   */
  async queryCorrection(correctionId: number, payCount: number) {
    const respData = validateCorrectionQueryRespData(
      await ArticleApis.correction(correctionId, false)
    );
    switch (respData.correction.status) {
      case CorrectStatus.notCorrect:
      case CorrectStatus.correcting:
        await new Promise<void>((resolve) => {
          setTimeout(resolve, 1000);
        });
        await this.queryCorrection(correctionId, payCount);
        break;
      case CorrectStatus.fail:
        this.showCorrectErr(
          Response.newForException(
            respData.correction.error_msg?.trim() || "纠错失败，请再次提交",
            `纠错记录状态为失败，纠错记录id为${correctionId}`
          )
        );
        break;
      case CorrectStatus.success:
        this.onCorrectSuccess(payCount, respData);
        break;
      default:
        this.showCorrectErr(
          Response.newForException(
            MSG_CORRECT_RESP_FORMAT_ERR,
            `未知纠错状态: ${respData.correction.status}`
          )
        );
    }
  }

  /**
   * 纠错成功时调用
   */
  onCorrectSuccess(
    payCount: number,
    respData: CorrectRespData | CorrectionRespData
  ) {
    this.correcting = false;
    const { article } = respData;
    const corrections = resolveCorrectRespCorrections(respData);
    this.savedArticle = { ...article, correction: corrections };
    let htmlContent = article.content;
    if (!htmlContent) {
      const currContent: Contents = this.parseCurrContent();
      htmlContent = currContent.html;
      logToServer("纠错成功后响应的文章html内容为空，文章id：" + article.id);
    }
    this.markCorrections(corrections, htmlContent);
    Message.success("纠错成功");
    this.updateStoreInfos({ contentHtml: "", title: "" });
    const user = resolveCorrectRespUser(respData);
    if (user) this.$store.commit(RootMutationNames.setCurrUser, user);
  }

  /**
   * 解析接口错误，返回面向用户的提示
   */
  resolveUserApiAlert(error: Response | any): {
    title: string;
    message: string;
  } {
    if (error instanceof Response) {
      const msg = error.msg || "";
      if (msg === "网络错误，检查网络状态后重试") {
        return {
          title: "网络异常",
          message: "网络连接异常，请检查网络后再次提交",
        };
      }
      if (msg === "请求超时，请重试") {
        return {
          title: "请求超时",
          message: "请求超时，请再次提交",
        };
      }
      if (msg === MSG_CORRECT_RESP_FORMAT_ERR) {
        return { title: "提交失败", message: MSG_CORRECT_RESP_FORMAT_ERR };
      }
      if (msg === "服务繁忙，可稍后再尝试或联系客服") {
        return {
          title: "服务繁忙",
          message: "服务繁忙，请稍后再试或再次提交",
        };
      }
      if (msg === "发生错误，可尝试刷新页面或联系客服") {
        return { title: "提交失败", message: MSG_CORRECT_RESP_FORMAT_ERR };
      }
      if (msg) {
        return { title: "纠错失败", message: error.userMsg };
      }
      return {
        title: "操作失败",
        message: "服务繁忙，请稍后再试或再次提交",
      };
    }
    logToServer("接口响应异常", error);
    return { title: "提交失败", message: MSG_CORRECT_RESP_FORMAT_ERR };
  }

  /**
   * 显示通用接口错误提示
   */
  showUserApiErr(error: Response | any, dialogTitle: string) {
    const { title, message } = this.resolveUserApiAlert(error);
    this.$alert(message, dialogTitle || title, { type: "error" });
  }

  /**
   * 纠错请求发生错误时调用
   */
  showCorrectErr(error: Response | any) {
    this.correcting = false;
    if (error instanceof Response && error.code === Response.CODE_SENTENCE_TOO_LONG) {
      this.showSentenceTooLong(error);
      return;
    }
    const { title, message } = this.resolveUserApiAlert(error);
    this.$alert(message, title, { type: "error" });
  }

  /**
   * 提示消耗和剩余的纠错次数，并确认是否继续纠错
   * @returns resolve参数为true时继续纠错，为false时前往充值界面，reject结果表示不继续，退出纠错操作
   */
  async showCountConfirm(): Promise<boolean> {
    if (!this.currUser) throw Error("确保currUser不为空再调用此方法");
    const aiFunCount = this.currUser.correctCount || 0;
    const testCount = this.currUser.correct_test_count || 0;
    if (aiFunCount === COUNT_NO_LIMIT) return true;
    const payCount = this.isLongTextModel ? this.correctCountToPay : 1;

    console.log("aiFunCount = ", aiFunCount);
    console.log("testCount = ", testCount);
    console.log("payCount = ", payCount);

    await this.$confirm(
      `${
        aiFunCount >= payCount
          ? `此操作将消耗${payCount}次AI功能次数，您当前拥有${aiFunCount}次，是否继续?`
          : aiFunCount + testCount >= payCount
          ? aiFunCount > 0
            ? `您当前剩余${aiFunCount}次AI功能次数，此操作将消耗完AI功能次数，并消耗${
                payCount - aiFunCount
              }次试用次数（当前拥有${testCount}次试用次数)，是否继续？`
            : `此操作将消耗${payCount}次试用次数，您当前拥有${testCount}次，是否继续？`
          : `您的AI功能次数不足，是否前往充值纠错次数?`
      }`,
      "提示",
      {
        confirmButtonText: aiFunCount + testCount >= payCount ? "继续" : "前往",
        type: "warning",
      }
    );
    return aiFunCount + testCount >= payCount;
  }

  /**
   * 显示计算纠错次数失败的错误
   */
  showCountFailErr(response: Response) {
    if (response.code === Response.CODE_SENTENCE_TOO_LONG) {
      this.showSentenceTooLong(response);
      return;
    }
    this.calculateCountStatus = CalculateStatus.fail;
    const showRecalculate = response.code === Response.CODE_EXCEPTION;
    this.calculateFailTipObj = {
      text: `${this.totalTextLenMsg}，` + response.userMsg,
      color: appColors.dangerColor,
      showRecalculate: showRecalculate,
    };
  }

  /**
   * 显示纠错“句子太长”错误
   */
  showSentenceTooLong(response: Response) {
    if (response.code !== Response.CODE_SENTENCE_TOO_LONG) return;
    this.calculateCountStatus = CalculateStatus.fail;
    this.failText = response.data.failText;
    this.calculateFailTipObj = {
      text: `${this.totalTextLenMsg}，` + response.userMsg,
      color: appColors.dangerColor,
      showRecalculate: false,
    };
  }

  /**
   * 检查是否编辑器内容长度或消耗的纠错次数是否超过允许的最大值，如果超过则
   * 提示对应的错误信息
   * @returns 是否超过允许最大值
   */
  showTextLenLimit(): boolean {
    const msg = this.isLongTextModel
      ? this.correctCountToPay > this.maxPayCountOnce
        ? this.countLimitMsg
        : null
      : this.textLength > this.maxLenOnceCorrect
      ? this.lenLimitMsg
      : null;
    if (msg === null) return false;

    Message.error({
      message: msg,
      center: true,
      showClose: true,
    });
    return true;
  }

  /**
   * 检查当前用户是否已经登录，如未登录则显示“未登录”错误提示
   * @returns 是否是未登录
   */
  showNotLogin(): boolean {
    if (!this.isLogined) {
      Message.error({
        message: "未登录",
        center: true,
        showClose: true,
      });
      return true;
    }
    return false;
  }

  /**
   * 检查当前文章标题或内容是否为空，如果为空则则提示对应“不能为空”
   * 的错误提示
   * @returns 文章标题或内容是否为空
   */
  showTitleContentEmpty(currTitle: string, currContent: Contents): boolean {
    if (isEmpty(currTitle, true)) {
      Message.error({
        message: "标题不能为空",
        center: true,
        showClose: true,
      });
      return true;
    }
    if (isEmpty(currContent.html, true) || isEmpty(currContent.text, true)) {
      Message.error({
        message: "文章内容不能为空",
        center: true,
        showClose: true,
      });
      return true;
    }
    return false;
  }

  /**
   * debounce计算当前编辑器纯文本长度
   */
  debounceTextLength = debounce(this.calculateTextLength, 200);

  /**
   * 计算当前文本长度
   */
  calculateTextLength() {
    //计算文本长度
    this.isUpdateSinceCalculate = true;
    const currContent = this.parseCurrContent();
    this.textLength = currContent.text ? currContent.text.length - 1 : 0;
  }

  /**
   * debounce计算当前编辑器内容纠错时会消耗的纠错次数
   */
  debouncePayCount = debounce(this.calculatePayCount, 500);

  /**
   * 计算当前编辑器内容纠错时会消耗的纠错次数
   */
  async calculatePayCount() {
    if (!this.isLongTextModel) return;
    //计算文本消耗的纠错次数
    const currContent = this.parseCurrContent();
    this.failText = null;
    if (!currContent || isEmpty(currContent.text)) {
      this.isUpdateSinceCalculate = false;
      this.correctCountToPay = 0;
      this.calculateCountStatus = CalculateStatus.success;
    } else {
      this.calculateCountStatus = CalculateStatus.doing;
      try {
        const respData = await ArticleApis.countCorrect(
          currContent.text,
          false
        );
        if (typeof respData.count === "undefined")
          throw "计算纠错次数接口返回的count为undefind";
        this.correctCountToPay = respData.count;
        this.calculateCountStatus = CalculateStatus.success;
      } catch (error) {
        if (error instanceof Response) this.showCountFailErr(error);
        else {
          logToServer("计算纠错次数时异常", error);
          this.calculateCountStatus = CalculateStatus.fail;
          this.calculateFailTipObj = this.defaultCalculateFailTipObj;
        }
      }
      this.isUpdateSinceCalculate = false;
    }
  }
}
